import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';

import { ArrowInline } from '@/lib/Icon';
import { cn } from '@/lib/cn';
import type { SupportRequest } from '@/types/api';
import type { NativeRequest, NativeRequestEvent } from '@/features/native-support/types';
import { nativeLabel, nativeOpen, nativeSteps } from '@/features/native-support/requestLabels';
export type Request = SupportRequest | NativeRequest;
const isNative = (request: Request): request is NativeRequest => 'label_key' in request;
const keyFor = (request: Request) => isNative(request) ? request.label_key : request.status;
const nameFor = (request: Request) => isNative(request) ? null : request.handler_first_name;
const timeFor = (request: Request) => isNative(request) ? request.latest_event_at : request.updated_at;
const labelFor = (request: Request) => isNative(request) ? nativeLabel(request.label_key) : STATUS_LABEL[request.status];
const summaryFor = (request: Request) => isNative(request) ? labelFor(request) : summaryLine(request);

import { STATUS_LABEL, openRequests, stepsFor, summaryLine, when } from '../requestStatus';

/**
 * Support tickets shown as read-only status ("requests"). Patients never
 * get a separate thread per ticket: every reply goes through the one
 * Support chat, and Sonny sorts which request it's about.
 */

function StatusTag({ request }: { request: Request }) {
  return (
    <span
      // Plain join, not cn(): tailwind-merge treats text-10 and a text colour
      // as one group and would drop the size.
      className={[
        'shrink-0 self-start px-7 py-4 border font-mono uppercase text-10 leading-100 whitespace-nowrap',
        keyFor(request) === 'waiting_on_you'
          ? 'bg-inverse border-line-strong text-ink-onInverse'
          : keyFor(request) === 'resolved'
            ? 'bg-page border-line-subtle text-ink-muted'
            : 'bg-sunken border-line text-ink-secondary',
      ].join(' ')}
    >
      {labelFor(request)}
    </span>
  );
}

function HandlerTile({ name, size = 22 }: { name: string | null; size?: number }) {
  return (
    <span
      aria-hidden
      className="shrink-0 flex items-center justify-center bg-accent text-white font-mono uppercase"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
    >
      {name ? name.charAt(0) : '·'}
    </span>
  );
}

/** Pinned under the Support header: one request in full, or up to two rows. */
export function RequestStrip({
  requests,
  onOpen,
  onViewAll,
}: {
  requests: Request[];
  onOpen: (id: string) => void;
  onViewAll: () => void;
}) {
  if (requests.length === 0) return null;
  if (requests.length === 1) {
    const r = requests[0];
    return (
      <button
        type="button"
        onClick={() => onOpen(r.id)}
        className="shrink-0 w-full flex items-center gap-10 px-15 py-10 border-b border-line-subtle bg-page hover:bg-raised text-left"
      >
        <HandlerTile name={nameFor(r)} size={28} />
        <span className="flex-1 min-w-0 flex flex-col gap-2">
          <span className="font-mono uppercase text-10 text-ink-muted">Your request</span>
          <span className="text-13 leading-120 truncate">{r.title}</span>
          <span className="text-12 text-ink-secondary">{summaryFor(r)}</span>
        </span>
        <StatusTag request={r} />
        <ArrowInline className="text-ink-muted" />
      </button>
    );
  }
  return (
    <div className="shrink-0 border-b border-line-subtle bg-page flex flex-col">
      <div className="flex items-center justify-between px-15 pt-8 pb-4">
        <span className="font-mono uppercase text-10 text-ink-muted">
          Your requests · {requests.length} open
        </span>
        <button
          type="button"
          onClick={onViewAll}
          className="h-28 flex items-center gap-6 text-12 text-ink hover:text-ink-secondary"
        >
          View all <ArrowInline />
        </button>
      </div>
      {requests.slice(0, 2).map((r) => (
        <button
          key={r.id}
          type="button"
          onClick={() => onOpen(r.id)}
          className="min-h-44 w-full flex items-center gap-10 px-15 py-8 border-t border-sunken hover:bg-raised text-left"
        >
          <HandlerTile name={nameFor(r)} />
          <span className="flex-1 min-w-0 text-13 truncate">{r.title}</span>
          <StatusTag request={r} />
          <ArrowInline className="text-ink-muted" />
        </button>
      ))}
    </div>
  );
}

function SubHeader({ backLabel, onBack, title }: { backLabel: string; onBack: () => void; title: string }) {
  return (
    <div className="shrink-0 flex items-center h-44 pl-5 pr-15 border-b border-line-subtle bg-raised">
      <button
        type="button"
        onClick={onBack}
        className="h-44 px-5 flex items-center gap-2 text-13 text-ink-secondary hover:text-ink"
      >
        <ChevronLeft size={18} strokeWidth={1.5} />
        {backLabel}
      </button>
      <span className="ml-auto font-mono uppercase text-12 text-ink-muted">{title}</span>
    </div>
  );
}

function Progress({ request }: { request: Request }) {
  if (isNative(request)) return <span aria-hidden className="grid grid-cols-3 gap-3 w-full">{['with_team', 'waiting_on_you', 'resolved'].map(key => <span key={key} className={cn('h-3', request.label_key === key ? 'bg-accent' : 'bg-[var(--line-default)]')} />)}</span>;
  return (
    <span aria-hidden className="grid grid-cols-4 gap-3 w-full">
      {stepsFor(request).map((s, i) => (
        <span
          key={i}
          className={cn(
            'h-3',
            s.state === 'done' ? 'bg-inverse' : s.state === 'current' ? 'bg-accent' : 'bg-[var(--line-default)]',
          )}
        />
      ))}
    </span>
  );
}

/** All requests: open ones as cards, past ones as compact rows. No composer. */
export function RequestsList({
  requests,
  onBack,
  onOpen,
}: {
  requests: Request[];
  onBack: () => void;
  onOpen: (id: string) => void;
}) {
  const [pastExpanded, setPastExpanded] = useState(false);
  const native = requests.some(isNative);
  const open = native ? requests.filter(r => isNative(r) && nativeOpen(r)) : openRequests(requests.filter((r): r is SupportRequest => !isNative(r)));
  const past = requests
    .filter((r) => isNative(r) ? !nativeOpen(r) : r.status === 'resolved')
    .sort((a, b) => Date.parse(b.resolved_at ?? timeFor(b)) - Date.parse(a.resolved_at ?? timeFor(a)) || a.id.localeCompare(b.id));
  return (
    <>
      <SubHeader backLabel="Chat" onBack={onBack} title="Your requests" />
      <div className="flex-1 min-h-0 overflow-y-auto px-15 py-20 flex flex-col gap-12">
        <span className="font-mono uppercase text-12 text-ink-muted">{native ? 'Open requests' : 'Open'} · {open.length}</span>
        {open.length === 0 && (
          <p className="text-13 text-ink-muted">Nothing open right now.</p>
        )}
        {open.map((r) => (
          <div key={r.id} className="shrink-0 border border-line-subtle bg-raised flex flex-col">
            <button
              type="button"
              onClick={() => onOpen(r.id)}
              className="w-full flex flex-col gap-10 px-15 py-12 text-left hover:bg-sunken"
            >
              <span className="flex items-start justify-between gap-10">
                <span className="text-15 leading-125">{r.title}</span>
                <StatusTag request={r} />
              </span>
              <Progress request={r} />
              <span className="flex items-center gap-8 text-12 text-ink-secondary">
                <HandlerTile name={nameFor(r)} />
                {summaryFor(r)} · Updated {when(timeFor(r))}
              </span>
            </button>
          </div>
        ))}
        {past.length > 0 && (
          <button type="button" aria-expanded={pastExpanded} onClick={() => setPastExpanded(value => !value)} className="mt-5 font-mono uppercase text-12 text-ink-muted text-left">Past requests</button>
        )}
        {(!native || pastExpanded) && past.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => onOpen(r.id)}
            className="shrink-0 w-full flex items-center justify-between gap-10 px-12 py-10 border border-line-subtle bg-raised text-13 text-ink-secondary text-left hover:bg-sunken"
          >
            <span className="min-w-0 truncate">{r.title}</span>
            <span className="shrink-0 text-12 text-ink-muted whitespace-nowrap">
              {isNative(r) ? labelFor(r) : r.resolved_at ? `Resolved ${when(r.resolved_at).split(' · ')[0]}` : 'Resolved'}
              {!isNative(r) && nameFor(r) ? ` · ${nameFor(r)}` : ''}
            </span>
          </button>
        ))}
      </div>
    </>
  );
}

/** One request's status: who's on it and the steps. */
export function RequestDetail({
  request,
  backLabel,
  onBack,
  events = [],
  hasMoreEvents = false,
  loadingMoreEvents = false,
  onLoadMoreEvents,
}: {
  request: Request;
  backLabel: string;
  onBack: () => void;
  events?: NativeRequestEvent[];
  hasMoreEvents?: boolean;
  loadingMoreEvents?: boolean;
  onLoadMoreEvents?: () => void;
}) {
  const steps = isNative(request) ? nativeSteps(request, events).map(step => ({ label: step.label, meta: step.at ? when(step.at) : '', state: step.current ? 'current' as const : step.at ? 'done' as const : 'todo' as const })) : stepsFor(request);
  const resolved = keyFor(request) === 'resolved';
  return (
    <>
      <SubHeader backLabel={backLabel} onBack={onBack} title="Request status" />
      <div className="flex-1 min-h-0 overflow-y-auto px-15 py-20 flex flex-col gap-15">
        <div className="shrink-0 flex flex-col items-start gap-8">
          <span className="font-mono uppercase text-12 text-ink-muted">Your request</span>
          <span className="text-18 leading-120">{request.title}</span>
          <StatusTag request={request} />
        </div>
        {!isNative(request) && <div className="shrink-0 flex items-center gap-10">
          <HandlerTile name={nameFor(request)} size={40} />
          <div className="flex flex-col gap-3">
            <span className="text-13">
              {nameFor(request)
                ? `${nameFor(request)} from System Support ${resolved ? 'handled' : 'is handling'} this`
                : resolved
                  ? 'Handled by System Support'
                  : 'Waiting for a teammate to pick this up'}
            </span>
            <span className="text-12 text-ink-muted">
              Updated {when(timeFor(request))}
            </span>
          </div>
        </div>}
        <ol className="shrink-0 flex flex-col">
          {steps.map((s, i) => (
            <li key={i} className="relative flex gap-12 pb-15 last:pb-0">
              {i < steps.length - 1 && (
                <span
                  aria-hidden
                  className={cn(
                    'absolute left-5 top-14 bottom-0 w-2',
                    s.state === 'done' ? 'bg-inverse' : 'bg-[var(--line-subtle)]',
                  )}
                />
              )}
              <span
                aria-hidden
                className={cn(
                  'relative z-[1] mt-2 w-12 h-12 shrink-0 border-2 bg-page',
                  s.state === 'done'
                    ? 'bg-inverse border-line-strong'
                    : s.state === 'current'
                      ? 'border-line-strong border-[3px]'
                      : 'border-[var(--line-default)]',
                )}
              />
              <div className="flex flex-col gap-2">
                <span className={`text-13 ${s.state === 'todo' ? 'text-ink-muted' : 'text-ink'}`}>
                  {s.label}
                </span>
                {s.meta && <span className="text-12 text-ink-muted">{s.meta}</span>}
              </div>
            </li>
          ))}
        </ol>
        {isNative(request) && hasMoreEvents && <button type="button" disabled={loadingMoreEvents} onClick={onLoadMoreEvents} className="shrink-0 h-40 text-12">View all</button>}
      </div>
    </>
  );
}

/** A request event (picked up / status change) inline in the Support chat. */
export function RequestUpdateCard({
  requestTitle,
  at,
  title,
  body,
  onViewStatus,
  compact = false,
}: {
  requestTitle: string;
  at: string;
  title: string;
  body?: string;
  onViewStatus: () => void;
  compact?: boolean;
}) {
  if (compact) return <button type="button" onClick={onViewStatus} className="mx-15 border border-line-subtle bg-page px-12 py-10 flex flex-col gap-4 text-left">
    <span className="font-mono uppercase text-10 text-ink-muted">{requestTitle} · {when(at)}</span>
    <span className="text-13 leading-130">{title}</span>
  </button>;
  return (
    <div className="mx-15 border border-line-subtle bg-page px-12 py-10 flex flex-col gap-4">
      <span className="font-mono uppercase text-10 text-ink-muted">
        {requestTitle} · {when(at).split(' · ')[1]}
      </span>
      <span className="text-13 leading-130">{title}</span>
      {body && <span className="text-12 leading-140 text-ink-secondary">{body}</span>}
      <button
        type="button"
        onClick={onViewStatus}
        className="self-start mt-4 h-24 flex items-center gap-6 text-12 text-ink hover:text-ink-secondary"
      >
        View status <ArrowInline />
      </button>
    </div>
  );
}
